import { isNumber } from 'lodash';
// import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { useCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-lodash-debounce';

import useAxiosHook from '../../../../../../hooks/useAxiosHook';
import { ClientLookupCollection } from '../../../../../../types/api/clients';
import {
  debounceTimeout,
  isStreetFreeInputAllowed,
} from '../../../../../../utils/constants/misc';
import { queryString } from '../../../../../../utils/helpers/http';
import { tryString } from '../../../../../../utils/helpers/parse';
import {
  formatMobileNumber,
  formatPhoneNumber,
  getPhoneorMobileNumberRegion,
} from '../../../../../../utils/helpers/phoneNumbers';
import AutoCompleteInput from '../../../../../Forms/AutoCompleteInput/AutoCompleteInput';
import FieldWithErrors from '../../../../../Forms/ReactHookForm/FieldWithErrors';

type Props = {
  orderIndex?: number;
  isConstant?: boolean;
};

function OrdererTab({ orderIndex, isConstant }: Props) {
  const { t } = useTranslation();
  const { setValue, watch } = useFormContext();
  const values = watch();
  // const ordererName = watch('ordererName');

  const debouncedOrderer = useDebounce(
    values.klient_naracatel_ime,
    debounceTimeout
  );

  const { data: orderersData, isLoading: isClientDataLoading } =
    useAxiosHook<ClientLookupCollection>(
      '/clients/lookup' +
        queryString({
          ime: debouncedOrderer,
          telefon: isNumber(values.klient_naracatel_telefon ?? '')
            ? values.klient_naracatel_telefon
            : null,
          page: 1,
          limit: 10,
        }),
      { skipWhen: false }
    );

  const clientsSuggestions =
    orderersData?.data.map((client) => {
      return {
        label: client.ime,
        value: String(client.id),
      };
    }) ?? [];

  const handleClientSelect = useCallback(
    (id: string | null) => {
      setValue('klient_naracatel_id', id);

      const orderer = orderersData?.data.find(
        (orderer) => String(orderer.id) === id
      );

      setValue('telefon_naracatel', orderer?.telefon ?? '');
      setValue('mobilen_naracatel', orderer?.mobilen ?? '');

      setValue('orderer_address', orderer?.address ?? '');
    },
    [orderersData?.data, setValue]
  );

  const handleClientChange = useCallback(
    (ime: string) => {
      setValue('klient_naracatel_ime', ime);
    },
    [setValue]
  );

  return (
    <>
      {orderIndex !== 0 && isConstant && <div className="constant" />}

      <FieldWithErrors name="klient_naracatel_id" label={t('Search Orderer')}>
        <AutoCompleteInput
          // disabled={isSenderTabDisabled}
          placeholder={
            isClientDataLoading
              ? t('Loading...')
              : t('Search by name or phone number')
          }
          options={clientsSuggestions}
          value={tryString(values.klient_naracatel_id) ?? null}
          filterValue={values.klient_naracatel_ime ?? ''}
          onFilterChange={handleClientChange}
          onSelectionChange={handleClientSelect}
          filterDataCy="orderer"
          optionsClassName="data-cy-orderer-options"
        />
        {/* <Controller
          name="klient_naracatel_id"
          render={({ field }) => (
            <Dropdown
              id="klient_naracatel_id"
              name="klient_naracatel_id"
              options={clientsSuggestions}
              value={field.value}
              onChange={(e) => handleClientSelect(e.value)}
              filter
              filterPlaceholder={t('Search')}
              placeholder={
                isClientDataLoading
                  ? t('Loading...')
                  : t('Search by name or phone number')
              }
              className="data-cy-klient_naracatel_id"
            />
          )}
        /> */}
      </FieldWithErrors>

      <FieldWithErrors name="orderer_address.placeName" label={t('Place')}>
        <Controller
          name="orderer_address.placeName"
          render={({ field }) => (
            <InputText
              id="orderer_address.placeName"
              name="orderer_address.placeName"
              value={field.value}
              disabled
            />
          )}
        />
      </FieldWithErrors>

      {isStreetFreeInputAllowed ? (
        <FieldWithErrors name="orderer_address.rawName" label={t('Place')}>
          <Controller
            name="orderer_address.rawName"
            render={({ field }) => (
              <InputText
                id="orderer_address.rawName"
                name="orderer_address.rawName"
                value={field.value}
                disabled
              />
            )}
          />
        </FieldWithErrors>
      ) : (
        <>
          <FieldWithErrors
            name="orderer_address.streetName"
            label={t('Street')}
          >
            <Controller
              name="orderer_address.streetName"
              render={({ field }) => (
                <InputText
                  id="orderer_address.streetName"
                  name="orderer_address.streetName"
                  value={field.value}
                  disabled
                />
              )}
            />
          </FieldWithErrors>

          <div className="p-grid">
            <FieldWithErrors
              name="orderer_address.housenumber"
              className="p-col-4"
              label={false}
            >
              <Controller
                name="orderer_address.housenumber"
                render={({ field }) => (
                  <InputText
                    id="orderer_address.housenumber"
                    name="orderer_address.housenumber"
                    value={field.value}
                    disabled
                    placeholder={t('Street No.')}
                  />
                )}
              />
            </FieldWithErrors>

            <FieldWithErrors
              name="orderer_address.entrance"
              className="p-col-4"
              label={false}
            >
              <Controller
                name="orderer_address.entrance"
                render={({ field }) => (
                  <InputText
                    id="orderer_address.entrance"
                    name="orderer_address.entrance"
                    value={field.value}
                    disabled
                    placeholder={t('Entrance No.')}
                  />
                )}
              />
            </FieldWithErrors>

            <FieldWithErrors
              name="orderer_address.apartment"
              className="p-col-4"
              label={false}
            >
              <Controller
                name="orderer_address.apartment"
                render={({ field }) => (
                  <InputText
                    id="orderer_address.apartment"
                    name="orderer_address.apartment"
                    value={field.value}
                    disabled
                    placeholder={t('Flat No.')}
                  />
                )}
              />
            </FieldWithErrors>
          </div>
        </>
      )}

      <FieldWithErrors name="mobilen_naracatel" label={t('Mobile')}>
        <Controller
          name="mobilen_naracatel"
          render={({ field }) => (
            <InputText
              id="mobilen_naracatel"
              name="mobilen_naracatel"
              value={formatMobileNumber(
                field.value ?? '',
                getPhoneorMobileNumberRegion(field.value)
              )}
              disabled
            />
          )}
        />
      </FieldWithErrors>
      <FieldWithErrors name="telefon_naracatel" label={t('Phone')}>
        <Controller
          name="telefon_naracatel"
          render={({ field }) => (
            <InputText
              id="telefon_naracatel"
              name="telefon_naracatel"
              value={formatPhoneNumber(
                field.value ?? '',
                getPhoneorMobileNumberRegion(field.value)
              )}
              disabled
            />
          )}
        />
      </FieldWithErrors>
    </>
  );
}

export default OrdererTab;
